import { useCallback, useState } from 'react';

import { useDebounce } from '@/hooks/useDebounce';

const SEARCH_DEBOUNCE_MS = 400;

/**
 * Whether a search field's result list is open, and what it searches. Escape closes the list
 * without clearing what was typed; typing again reopens it. The chevron opens the list on the first
 * page before anything is typed: a click reads as "show me who I can pick", typing then narrows it
 * down.
 */
export function useEmployeeSearchList() {
    const [searchText, setSearchText] = useState('');
    const [isListDismissed, setIsListDismissed] = useState(false);
    const [isBrowsing, setIsBrowsing] = useState(false);
    const debouncedSearch = useDebounce(searchText, SEARCH_DEBOUNCE_MS);
    const isListOpen = (debouncedSearch.trim().length > 0 || isBrowsing) && !isListDismissed;

    const type = (text: string) => {
        setSearchText(text);
        setIsListDismissed(false);
    };

    // Stable, so a listener that closes the list need not be bound again on every render.
    const dismiss = useCallback(() => {
        setIsBrowsing(false);
        setIsListDismissed(true);
    }, []);

    const toggle = () => {
        if (isListOpen) {
            dismiss();
            return;
        }
        setIsBrowsing(true);
        setIsListDismissed(false);
    };

    const clear = () => {
        setSearchText('');
        setIsBrowsing(false);
    };

    return { searchText, debouncedSearch, isListOpen, type, dismiss, toggle, clear };
}

export type EmployeeSearchList = ReturnType<typeof useEmployeeSearchList>;
